import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { getPriorityBadge } from "../utils/helpers";

export default function CommentPanel({ taskId, taskName, priority, onClose }) {
  const { getComments, addComment, getAttachments } = useData();
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [comments, attachments] = await Promise.all([
        getComments(taskId),
        getAttachments(taskId),
      ]);
      setComments(comments);
      setAttachments(attachments);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [taskId, getComments, getAttachments]);

  useEffect(() => { load(); }, [load]);



  // Scroll to bottom inside panel only — not the page
  useEffect(() => {
    if (bottomRef.current) {
      const container = bottomRef.current.parentElement;
      container.scrollTop = container.scrollHeight;
    }
  }, [comments]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const res = await addComment(taskId, text.trim());
      setComments(p => [...p, res.comment]);
      setText("");
    } catch(e) { alert("Failed: " + e.message); }
    finally { setSending(false); }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const formatTime = (ts) => {
    const d = new Date(ts);
    const diffMins = Math.floor((Date.now() - d) / 60000);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins/60)}h ago`;
    return d.toLocaleDateString();
  };

  const roleColor = (role) => role === "admin" ? "#4F6EF7" : role === "supervisor" ? "#059669" : "#7C3AED";
  const roleBg    = (role) => role === "admin" ? "#EEF2FF" : role === "supervisor" ? "#F0FDF4" : "#F5F3FF";

  const modalContent = (
    <>
      <style>{`
        .comment-panel-input-area {
          padding: 12px 14px max(16px, env(safe-area-inset-bottom)) !important;
          flex-shrink: 0;
          border-top: 1px solid #F1F5F9;
          background: #FAFAFA;
        }
      `}</style>

      <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      {/* Panel */}
      <div className="modal" style={{
        height: "85vh", width: "100%", maxWidth: 500,
        background:"white", boxShadow:"var(--shadow-xl)",
        borderRadius: "var(--radius-xl)", border: "none",
        display:"flex", flexDirection:"column", fontFamily:"inherit",
        overflow:"hidden", boxSizing:"border-box", margin: "auto"
      }}>
        {/* Header */}
        <div style={{
          padding:"18px 18px 14px", borderBottom:"1px solid #F1F5F9",
          background:"linear-gradient(135deg,#4F6EF7,#6C8EFF)", color:"white", flexShrink:0,
        }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:10, fontWeight:700, opacity:0.8, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:4 }}>
                💬 Task Comments
              </div>
              <div style={{ fontSize:14, fontWeight:700, lineHeight:1.3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:8 }}>
                {taskName}
                {(() => {
                  const pb = getPriorityBadge(priority);
                  return (
                    <span style={{ 
                      fontSize: 9, padding: "2px 6px", borderRadius: 10, 
                      background: "rgba(255,255,255,0.2)", color: "white", 
                      border: "1px solid rgba(255,255,255,0.3)", fontWeight: 600 
                    }}>
                      {pb.label}
                    </span>
                  );
                })()}
              </div>
              <div style={{ fontSize:11, opacity:0.7, marginTop:3 }}>
                {comments.length} message{comments.length !== 1 ? "s" : ""}
              </div>
            </div>
            <button onClick={onClose} style={{
              background:"rgba(255,255,255,0.2)", border:"none", color:"white",
              width:32, height:32, borderRadius:8, cursor:"pointer", fontSize:18,
              display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginLeft:10,
            }}>×</button>
          </div>
        </div>

        {/* Messages */}
        <div style={{ flex:"1 1 0", minHeight:0, overflowY:"auto", overflowX:"hidden", padding:"16px 14px", display:"flex", flexDirection:"column", gap:12, scrollBehavior:"smooth" }}>
          {attachments.length > 0 && (
          <div style={{ padding:"10px 14px", background:"#FFFBEB", borderBottom:"1px solid #FEF3C7", flexShrink:0 }}>
            <div style={{ fontSize:10, fontWeight:700, color:"#92400E", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 }}>📎 Attached Files</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {attachments.map((a, i) => (
                <div key={i} style={{
                  display:"flex", alignItems:"center", gap:5,
                  background:"white", border:"1px solid #FEF3C7",
                  borderRadius:8, padding:"4px 10px", fontSize:12,
                }}>
                  <span>📄</span>
                  <a href={`https://task-system.aquint-automation.workers.dev/api/download/${a.fileToken}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color:"#1D4ED8", maxWidth:160, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", textDecoration:"none" }}>
                    {a.fileName} ↓
                  </a>
                  <span style={{ color:"#94A3B8", fontSize:10 }}>({(a.fileSize/1024).toFixed(0)}KB)</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {loading && (
            <div style={{ textAlign:"center", color:"#94A3B8", fontSize:13, padding:40 }}>
              Loading comments...
            </div>
          )}
          {!loading && comments.length === 0 && (
            <div style={{ textAlign:"center", padding:"50px 20px" }}>
              <div style={{ fontSize:40, marginBottom:10 }}>💬</div>
              <div style={{ fontSize:13, color:"#94A3B8", fontWeight:600 }}>No comments yet</div>
              <div style={{ fontSize:12, color:"#CBD5E1", marginTop:4 }}>Be the first to comment on this task</div>
            </div>
          )}
          {comments.map((c) => {
            const isMe = c.authorId === user?.userId;
            return (
              <div key={c.id} style={{
                display:"flex", gap:9,
                flexDirection: isMe ? "row-reverse" : "row",
                alignItems:"flex-end",
              }}>
                <div style={{
                  width:32, height:32, borderRadius:"50%", flexShrink:0,
                  background: roleColor(c.authorRole),
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:11, fontWeight:700, color:"white",
                }}>
                  {c.initials || c.authorName?.[0]?.toUpperCase() || "?"}
                </div>
                <div style={{ maxWidth:"72%", display:"flex", flexDirection:"column", gap:3, alignItems: isMe ? "flex-end" : "flex-start" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:6, flexDirection: isMe ? "row-reverse" : "row" }}>
                    <span style={{ fontSize:11, fontWeight:700, color:"#374151" }}>{isMe ? "You" : c.authorName}</span>
                    <span style={{
                      fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:10,
                      background: roleBg(c.authorRole), color: roleColor(c.authorRole),
                      textTransform:"uppercase", letterSpacing:"0.05em",
                    }}>{c.authorRole}</span>
                  </div>
                  <div style={{
                    padding:"10px 14px",
                    borderRadius: isMe ? "14px 4px 14px 14px" : "4px 14px 14px 14px",
                    background: isMe ? "linear-gradient(135deg,#4F6EF7,#6C8EFF)" : "#F8FAFC",
                    color: isMe ? "white" : "#1E293B",
                    fontSize:13, lineHeight:1.55,
                    border: isMe ? "none" : "1px solid #E2E8F0",
                    boxShadow:"0 1px 4px rgba(0,0,0,0.06)",
                    whiteSpace:"pre-wrap", wordBreak:"break-word",
                  }}>
                    {c.text}
                  </div>
                  <span style={{ fontSize:10, color:"#94A3B8" }}>{formatTime(c.timestamp)}</span>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="comment-panel-input-area">
          <div style={{ display:"flex", gap:8, alignItems:"flex-end" }}>
            <div style={{
              width:34, height:34, borderRadius:"50%", flexShrink:0,
              background: user?.role === "admin" ? "#4F6EF7" : "#7C3AED",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:11, fontWeight:700, color:"white", overflow:"hidden",
            }}>
              {user?.avatar
                ? <img src={user.avatar} alt="" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                : (user?.initials || user?.name?.[0]?.toUpperCase() || "?")}
            </div>
            <div style={{ flex:1, position:"relative" }}>
              <textarea
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={handleKey}
                placeholder="Write a comment... (Enter to send)"
                rows={2}
                style={{
                  width:"100%", padding:"10px 46px 10px 13px",
                  border:"1.5px solid #E2E8F0", borderRadius:12,
                  fontSize:13, fontFamily:"inherit", outline:"none",
                  resize:"none", lineHeight:1.5, background:"white",
                  color:"#1E293B", boxSizing:"border-box", transition:"border-color 0.2s",
                }}
                onFocus={e => e.target.style.borderColor="#4F6EF7"}
                onBlur={e => e.target.style.borderColor="#E2E8F0"}
              />
              <button onClick={send} disabled={!text.trim() || sending} style={{
                position:"absolute", right:8, bottom:8,
                width:30, height:30, borderRadius:8, border:"none",
                background: text.trim() ? "#4F6EF7" : "#E2E8F0",
                color: text.trim() ? "white" : "#94A3B8",
                cursor: text.trim() ? "pointer" : "default",
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:16, fontWeight:700, transition:"all 0.2s",
              }}>
                {sending ? "⏳" : "↑"}
              </button>
            </div>
          </div>
          <div style={{ fontSize:10, color:"#94A3B8", marginTop:5, marginLeft:42 }}>
            Enter to send · Shift+Enter for new line
          </div>
        </div>
      </div>
    </div>
    </>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
